import React, { useEffect, useState } from "react";
import "./styles/MD_ManageTopics.css";
import Loading from "../../Common/Loading";

interface TopicRow {
  Yr: number;
  Strand: string;
  StrandDesc: string;
  SubStrand: string;
  SubStrandDesc: string;
  Topic: string;
  TopicName: string;
  LessonCode: string;
  OldTopic?: number;
  TopicOrder?: number;
  NoOfQns: number;
  Remarks: string;
}

const API_BASE = process.env.REACT_APP_DOCUMENT_LINK;

// Helper: get session login stored in sessionStorage/sessionStorage by your auth flow
function getSessionLogin(): string {
  // sessionStorage key: "L"  |  sessionStorage key: "UserName"
  return sessionStorage.getItem("UserName") || "";
}

const MD_ManageTopics: React.FC = () => {
  const [countries, setCountries] = useState<string[]>([]);
  const [country, setCountry] = useState("");
  const [rows, setRows] = useState<TopicRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<TopicRow | null>(null);
  const [insertMode, setInsertMode] = useState<"B" | "A" | null>(null);
  const [loading, setLoading] = useState(false);
  const [strands, setStrands] = useState<string[]>([]);
  const [subStrands, setSubStrands] = useState<string[]>([]);
  const [lessonSearch, setLessonSearch] = useState("");
  const [updateLessonSearch, setUpdateLessonSearch] = useState("");

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState<string | null>(null);

  // Both dropdowns (Add + Update) use the SAME blob folder list — same as VB
  const [blobLessons, setBlobLessons] = useState<string[]>([]);

  // Add Live Topic Form
  // strand/substrand here are loaded dynamically in VB (TPGM003); 
  // keeping them as simple dropdowns matching VB default values
  const [addForm, setAddForm] = useState({
    strand: "",
    substrand: "",
    year: "K00",
    lesson: ""
  });

  // Update Live Topic Form
  // sourceLesson = the blob folder chosen in the dropdown (ddlupdatedlsns)
  // destLessonCode = selected row's LessonCode (hidLessonCode) — fixed from previous bug
  const [updateForm, setUpdateForm] = useState({
    sourceLesson: ""   // chosen from dropdown; dest comes from selectedRow.LessonCode
  });

  // Manual Insert Form
  const [newForm, setNewForm] = useState({
    topicName: "",
    lessonCode: "",
  });

  // Edit Form (bottom panel)
  const [editForm, setEditForm] = useState({
    strandDesc: "",
    subStrandDesc: "",
    topicName: "",
    lessonCode: "",
    year: "",
  });


  const searchLesson = (value: string) => {
    setLessonSearch(value);

    const search = value.toUpperCase().trim();

    if (!search) {
      return;
    }

    const index = blobLessons.findIndex(
      lesson => lesson.toUpperCase() === search
    );

    if (index !== -1) {
      setAddForm(prev => ({
        ...prev,
        lesson: blobLessons[index]
      }));
    }
  };

  const searchUpdateLesson = (value: string) => {
    setUpdateLessonSearch(value);

    const search = value.toUpperCase().trim();

    if (!search) {
      return;
    }

    const index = blobLessons.findIndex(
      lesson => lesson.toUpperCase() === search
    );

    if (index !== -1) {
      setUpdateForm({
        sourceLesson: blobLessons[index]
      });
    }
  };

  const loadSubStrands = async (strand: string) => {
    if (!strand) {
      setSubStrands([]);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${API_BASE}api/md/substrands_MT/?strand=${encodeURIComponent(strand)}`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const d = await response.json();

      const substrands = d.substrands || [];

      setSubStrands(substrands);

      if (substrands.length > 0) {
        setAddForm(prev => ({
          ...prev,
          substrand: substrands[0]
        }));
      } else {
        setAddForm(prev => ({
          ...prev,
          substrand: ""
        }));
      }
    } catch (error) {
      console.error("Error loading substrands:", error);
      setSubStrands([]);

      setAddForm(prev => ({
        ...prev,
        substrand: ""
      }));

      alert("Failed to load SubStrands.");
    } finally {
      setLoading(false);
    }
  };

  const loadStrands = async () => {
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}api/md/strands_MT/`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const d = await response.json();

      const strandList = d.strands || [];

      setStrands(strandList);

      if (strandList.length > 0) {
        setAddForm(prev => ({
          ...prev,
          strand: strandList[0],
          substrand: ""
        }));

        await loadSubStrands(strandList[0]);
      } else {
        setAddForm(prev => ({
          ...prev,
          strand: "",
          substrand: ""
        }));

        setSubStrands([]);
      }
    } catch (error) {
      console.error("Error loading strands:", error);
      setStrands([]);
      setSubStrands([]);

      setAddForm(prev => ({
        ...prev,
        strand: "",
        substrand: ""
      }));

      alert("Failed to load Strands.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.body.style.backgroundColor = "#ffffff";
    document.body.style.fontFamily = "Arial";

    const loadCountries = async () => {
      setLoading(true);

      try {
        const response = await fetch(`${API_BASE}api/md/countries/`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const d = await response.json();

        setCountries(d.countries || []);
      } catch (error) {
        console.error("Error loading countries:", error);
        setCountries([]);
        alert("Failed to load countries.");
      } finally {
        setLoading(false);
      }
    };

    loadCountries();
  }, []);

const loadTopics = (c: string) => {
    setLoading(true);

    setCountry(c);
    setSelectedRow(null);

    setEditForm({
        strandDesc: "",
        subStrandDesc: "",
        topicName: "",
        lessonCode: "",
        year: ""
    });

    if (!c || c === "---Select Country---") {
        setRows([]);
        setLoading(false);
        return;
    }

    Promise.all([
        fetch(`${API_BASE}api/md/topics/?country=${encodeURIComponent(c)}`).then(r => r.json()),
        fetch(`${API_BASE}api/md/blob_folders/`).then(r => r.json())
    ])
    .then(([topics, blobs]) => {
        setRows(topics.rows || []);
        setBlobLessons(blobs.lessons || []);
    })
    .finally(() => setLoading(false));
};
  const selectRow = (r: TopicRow) => {
    setSelectedRow(r);
    setInsertMode(null);
    setEditForm({
      strandDesc: r.StrandDesc,
      subStrandDesc: r.SubStrandDesc,
      topicName: r.TopicName,
      lessonCode: r.LessonCode,
      year: String(r.Yr),
    });
  };

  // -------------------------------------------------------------------------
  // Add Live Topic  (btnok_Click)
  // -------------------------------------------------------------------------
  // const handleAddLiveTopic = () => {
  //   if (!addForm.lesson) { alert("Please select a lesson"); return; }

  //   setLoading(true);
  //   const f = new FormData();
  //   f.append("country", country);
  //   f.append("strand", addForm.strand);
  //   f.append("substrand", addForm.substrand);
  //   f.append("year", addForm.year);       // e.g. "K01"
  //   f.append("lesson", addForm.lesson);
  //   f.append("login", getSessionLogin());

  //   fetch(`${API_BASE}api/md/add_live/`, { method: "POST", body: f })
  //     .then(r => r.json())
  //     .then(d => {
  //       setLoading(false);
  //       if (d.status === "ok") {
  //         setShowAddModal(false);
  //         setShowSuccessModal(`The lesson was successfully added as ${d.lessonName}`);
  //         loadTopics(country);
  //       } else {
  //         alert("Error: " + d.message);
  //       }
  //     })
  //     .catch(err => {
  //       setLoading(false);
  //       alert("Network error: " + err);
  //     });
  // };


  const handleAddLiveTopic = async () => {
    if (!addForm.lesson) {
      alert("Please select a lesson");
      return;
    }

    setLoading(true);

    try {
      const f = new FormData();
      f.append("country", country);
      f.append("strand", addForm.strand);
      f.append("substrand", addForm.substrand);
      f.append("year", addForm.year);
      f.append("lesson", addForm.lesson);
      f.append("login", getSessionLogin());

      const response = await fetch(`${API_BASE}api/md/add_live/`, {
        method: "POST",
        body: f
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const d = await response.json();

      if (d.status === "ok") {
        setShowAddModal(false);
        setShowSuccessModal(
          `The lesson was successfully added as ${d.lessonName}`
        );

        await loadTopics(country);
      } else {
        alert("Error: " + d.message);
      }
    } catch (error) {
      console.error("Add Live Topic error:", error);
      alert("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  // -------------------------------------------------------------------------
  // Update Live Topic  (btnupdateok_Click)
  // Key fix: destLessonCode = selectedRow.LessonCode (hidLessonCode in VB)
  //          sourceLesson   = ddlupdatedlsns.SelectedValue (the chosen blob folder)
  // -------------------------------------------------------------------------
  const handleUpdateLiveTopic = () => {
    if (!updateForm.sourceLesson || updateForm.sourceLesson === "--SELECT--") {
      alert("Please select the lesson that you want to update");
      return;
    }
    if (!selectedRow) {
      alert("Please select a topic row first");
      return;
    }

    setLoading(true);
    const f = new FormData();
    f.append("country", country);
    f.append("destLessonCode", selectedRow.LessonCode);   // hidLessonCode
    f.append("sourceLesson", updateForm.sourceLesson);     // ddlupdatedlsns value
    f.append("login", getSessionLogin());

    fetch(`${API_BASE}api/md/update_live/`, { method: "POST", body: f })
      .then(r => r.json())
      .then(d => {
        setLoading(false);
        if (d.status === "ok") {
          setShowUpdateModal(false);
          setShowSuccessModal(d.message);
          loadTopics(country);
        } else {
          // VB shows the message in the success panel even for "Please select" case
          setShowUpdateModal(false);
          setShowSuccessModal(d.message);
        }
      })
      .catch(err => {
        setLoading(false);
        alert("Network error: " + err);
      });
  };

  // -------------------------------------------------------------------------
  // Open Update Modal — mirrors VB openUpdatelesson():
  // Pre-selects the dropdown to match the currently selected row's LessonCode
  // -------------------------------------------------------------------------
  const openUpdateModal = () => {
    if (!country || country.startsWith("-")) {
      alert("Please select a country");
      return;
    }
    // Pre-select dropdown to match selected row's lesson code (VB behaviour)
    const preSelect = selectedRow ? selectedRow.LessonCode : "";
    const matchedLesson = blobLessons.find(l => l === preSelect) || "";
    setUpdateForm({ sourceLesson: matchedLesson });
    setShowUpdateModal(true);
  };

  // -------------------------------------------------------------------------
  // Manual Insert  (btnAddNewTopic_Click)
  // -------------------------------------------------------------------------
  const insertTopic = () => {
    if (!selectedRow || !insertMode) return;
    const topicOrder = insertMode === "B"
      ? (selectedRow.TopicOrder ?? 0) - 1
      : (selectedRow.TopicOrder ?? 0) + 1;

    const f = new FormData();
    f.append("country", country);
    f.append("year", String(selectedRow.Yr));
    f.append("strand", selectedRow.Strand);
    f.append("substrand", selectedRow.SubStrand);
    f.append("topicName", newForm.topicName);
    f.append("lessonCode", newForm.lessonCode);
    f.append("topicOrder", String(topicOrder));

    fetch(`${API_BASE}api/md/add/`, { method: "POST", body: f })
      .then(() => {
        setInsertMode(null);
        setNewForm({ topicName: "", lessonCode: "" });
        loadTopics(country);
      });
  };

  // -------------------------------------------------------------------------
  // Save Edit  (btnSave_Click — PGM-460: only TopicName is editable)
  // -------------------------------------------------------------------------
  const saveEdit = () => {
    if (!selectedRow) return;
    const f = new FormData();
    f.append("country", country);
    f.append("yr", editForm.year);
    f.append("strand", selectedRow.Strand);
    f.append("substrand", selectedRow.SubStrand);
    f.append("topic", selectedRow.Topic);
    f.append("topicname", editForm.topicName);
    f.append("lessoncode", editForm.lessonCode);

    fetch(`${API_BASE}api/md/save/`, { method: "POST", body: f })
      .then(() => loadTopics(country));
  };

  // -------------------------------------------------------------------------
  // Delete Topic  (btnDeleteTopic_Click)
  // -------------------------------------------------------------------------
  const deleteTopic = () => {
    if (!selectedRow || !window.confirm("Do you really want to delete the selected Topic?")) return;
    const f = new FormData();
    f.append("country", country);
    f.append("yr", String(selectedRow.Yr));
    f.append("strand", selectedRow.Strand);
    f.append("substrand", selectedRow.SubStrand);
    f.append("topic", selectedRow.Topic);

    fetch(`${API_BASE}api/md/delete/`, { method: "POST", body: f })
      .then(() => loadTopics(country));
  };

  // Grid height adjustment
  useEffect(() => {
    const resizeGrid = () => {
      const grid = document.getElementById("lst_divScrollableTable");
      const header = document.querySelector(".md-top-table") as HTMLElement;
      if (!grid || !header) return;
      const h = window.innerHeight - header.offsetHeight - 170;
      grid.style.height = `${h}px`;
      grid.style.width = `${window.innerWidth - 30}px`;
    };
    window.addEventListener("resize", resizeGrid);
    resizeGrid();
    return () => window.removeEventListener("resize", resizeGrid);
  }, [country, selectedRow, insertMode]);

  // -------------------------------------------------------------------------
  // Styles
  // -------------------------------------------------------------------------
  const headerStyle: React.CSSProperties = {
    position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#c7ddfd'
  };

  const overlayStyle: React.CSSProperties = {
    position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 9999
  };

  const modalBoxStyle: React.CSSProperties = {
    backgroundColor: "#ffffff", borderRadius: "6px",
    boxShadow: "0 4px 15px rgba(0,0,0,0.4)", minWidth: "450px",
    overflow: "hidden", fontFamily: "Arial, sans-serif"
  };

  const modalHeaderStyle: React.CSSProperties = {
    backgroundColor: "#e0e0e0", textAlign: "center", padding: "10px",
    fontWeight: "bold", fontSize: "14px", borderBottom: "1px solid #ccc", color: "#000"
  };

  const modalContentStyle: React.CSSProperties = {
    padding: "20px 30px", backgroundColor: "#fff"
  };

  const mutedInputStyle: React.CSSProperties = {
    backgroundColor: "#f0f0f0", color: "#6d6d6d",
    border: "1px solid #d0d0d0", fontWeight: "normal", width: "90%", padding: "2px"
  };

  const btnStyle: React.CSSProperties = {
    marginRight: "6px", cursor: "pointer"
  };

  return (
    <div>
      {/* Loading overlay */}
      {loading && <Loading />}
      {/* Top toolbar */}
      <table className="md-top-table">
        <tbody>
          <tr>
            <td colSpan={2} className="md-header">
              Manage Strand / SubStrand / Topic / Questions
            </td>
          </tr>
          <tr>
            <td style={{ fontSize: "16px" }}>
              Curriculum:&nbsp;
              <select
                onChange={e => loadTopics(e.target.value)}
                value={country}
                disabled={loading}
                style={{ cursor: loading ? "wait" : "pointer" }}
              >
                <option>---Select Country---</option>
                {countries.map(c => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </td>
            <td align="right">
              {/* Add Topic — enabled only when country selected (mirrors VB) */}
              <button
                id="buttonT"
                style={btnStyle}
                onClick={() => {
                  if (!country || country.startsWith("-")) {
                    alert("Please select a country");
                    return;
                  }

                  setAddForm({
                    strand: "",
                    substrand: "",
                    year: "K00",
                    lesson: ""
                  });

                  setLessonSearch("");
                  setSubStrands([]);

                  loadStrands();
                  setShowAddModal(true);
                }}
              >
                Add Topic
              </button>

              {/* Update — enabled only when country selected AND a row is selected */}
              <button
                id="buttonT"
                style={btnStyle}
                onClick={openUpdateModal}
                disabled={!selectedRow}
              >
                Update
              </button>

              {/* Insert Before / After — only visible when row selected (VB: display:none until row selected) */}
              {/* {selectedRow && (
                <>
                  <button
                    id="buttonT"
                    style={btnStyle}
                    onClick={() => { setInsertMode("B"); setNewForm({ topicName: "", lessonCode: "" }); }}
                  >
                    Insert Topic - Before
                  </button>
                  <button
                    id="buttonT"
                    style={btnStyle}
                    onClick={() => { setInsertMode("A"); setNewForm({ topicName: "", lessonCode: "" }); }}
                  >
                    Insert Topic - After
                  </button>
                </>
              )} */}

              <button
                id="buttonT"
                style={btnStyle}
                disabled={!selectedRow}
                onClick={deleteTopic}
              >
                Delete Topic
              </button>

              <button
                id="buttonT"
                style={btnStyle}
                disabled={!selectedRow}
                onClick={() => {
                  const params = new URLSearchParams({
                    Country: country,
                    Yr: String(selectedRow?.Yr),
                    Strand: selectedRow?.Strand || "",
                    SubStrand: selectedRow?.SubStrand || "",
                    Topic: selectedRow?.Topic || "",
                    LessonCode: selectedRow?.LessonCode || ""
                  });
                  window.open(`/WebAdmin/MATHSDOCTOR/MD_AddDeleteQn?${params.toString()}`, "_blank");
                }}
              >
                Questions
              </button>

              <button
                id="buttonT"
                style={btnStyle}
                onClick={() => window.location.href = "/SysAdmin"}
              >
                Back to Menu
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      {/* Grid */}
      {country && (
        <div className="md-grid-wrapper">
          <div style={{
            fontFamily: "Arial", fontWeight: "bold", fontSize: "10pt",
            padding: "8px 6px", borderBottom: "1px solid #80a0ff",
            backgroundColor: "#c7ddfd", color: "#2859ac", textAlign: "center", width: "100vw"
          }}>
            Topics For Country : {country}
          </div>
          <div className="clsGridContainer" id="lst_divScrollableTable">
            <table className="clsGridTable">
              <thead className="clsGridHeader">
                <tr style={{ backgroundColor: "pink" }}>
                  <th className="col-slno" style={headerStyle}>#</th>
                  <th className="col-strand" style={headerStyle}>Strand</th>
                  <th className="col-substrand" style={headerStyle}>SubStrand</th>
                  <th className="col-year" style={headerStyle}>Year</th>
                  <th className="col-topic" style={headerStyle}>Topic</th>
                  <th className="col-topicname" style={headerStyle}>TopicName</th>
                  <th className="col-lesson" style={headerStyle}>LessonCode</th>
                  <th className="col-lesson" style={headerStyle}>Old Topic</th>
                  <th className="col-lesson" style={headerStyle}>TopicOrder</th>
                  <th className="col-qns" style={headerStyle}>NoOfQns</th>
                  <th className="col-remarks" style={headerStyle}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const prev = i > 0 ? rows[i - 1] : null;
                  const isStrandMuted = prev && r.Strand === prev.Strand;
                  const isSubStrandMuted = prev && r.Strand === prev.Strand && r.SubStrand === prev.SubStrand;
                  const isYearMuted = prev && r.Yr === prev.Yr;
                  return (
                    <tr
                      key={i}
                      className={`clsGridRow ${selectedRow === r ? "clsGridSelected" : ""}`}
                      onClick={() => selectRow(r)}
                    >
                      <td className="col-slno">{i + 1}</td>
                      <td style={{ color: isStrandMuted ? "#cccccc" : "inherit" }}>
                        {r.Strand}) {r.StrandDesc}
                      </td>
                      <td style={{ color: isSubStrandMuted ? "#cccccc" : "inherit" }}>
                        {r.SubStrand}) {r.SubStrandDesc}
                      </td>
                      <td className="clsGridCenter" style={{ color: isYearMuted ? "#cccccc" : "inherit" }}>
                        {r.Yr}
                      </td>
                      <td>{r.Topic}</td>
                      <td>{r.TopicName}</td>
                      <td>{r.LessonCode}</td>
                      <td>{r.OldTopic}</td>
                      <td>{r.TopicOrder}</td>
                      <td className="clsGridCenter">{r.NoOfQns}</td>
                      <td style={{ color: "red" }}>{r.Remarks}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bottom panel — Edit selected topic (PGM-460: TopicName only editable) */}
      {selectedRow && !insertMode && (
        <div className="md-panel">
          <table width="100%">
            <tbody>
              <tr>
                <td width="10%">Strand:</td>
                <td width="35%">
                  <input className="TextStyle" value={editForm.strandDesc} readOnly disabled style={mutedInputStyle} />
                </td>
                <td width="10%">Sub Strand:</td>
                <td width="32%">
                  <input className="TextStyle" value={editForm.subStrandDesc} readOnly disabled style={mutedInputStyle} />
                </td>
                <td width="5%">Year:</td>
                <td width="8%">
                  <input className="TextStyle" value={editForm.year} readOnly disabled style={mutedInputStyle} />
                </td>
              </tr>
              <tr>
                <td>Topic Name:</td>
                <td>
                  <input
                    className="TextStyle"
                    value={editForm.topicName}
                    onChange={e => setEditForm({ ...editForm, topicName: e.target.value })}
                    style={{ width: "90%" }}
                  />
                </td>
                <td>Lesson Code:</td>
                <td>
                  <input className="TextStyle" value={editForm.lessonCode} readOnly disabled style={mutedInputStyle} />
                </td>
                <td colSpan={2}>
                  <button id="buttonT" onClick={saveEdit}>Save</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Manual Insert Panel */}
      {insertMode && selectedRow && (
        <div className="md-panel">
          <table width="100%">
            <tbody>
              <tr>
                <td width="15%">New Topic Name:</td>
                <td width="30%">
                  <input
                    className="TextStyle"
                    value={newForm.topicName}
                    onChange={e => setNewForm({ ...newForm, topicName: e.target.value })}
                  />
                </td>
                <td width="10%">Lesson Code:</td>
                <td width="15%">
                  <input
                    className="TextStyle"
                    value={newForm.lessonCode}
                    onChange={e => setNewForm({ ...newForm, lessonCode: e.target.value })}
                  />
                </td>
                <td width="25%">
                  Will be inserted {insertMode === "B" ? "before" : "after"} selected item
                </td>
                <td width="5%">
                  <button id="buttonT" onClick={insertTopic}>Save</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* ---- MODAL: Add Live Topic ---- */}
      {showAddModal && (
        <div style={overlayStyle}>
          <div style={modalBoxStyle}>
            <div style={modalHeaderStyle}>Adding of New MDLesson</div>
            <div style={modalContentStyle}>
              <table width="100%" cellPadding={4}>
                <tbody>
                  <tr>
                    <td align="right" style={{ fontSize: "14px" }}>Strand:</td>
                    <td>
                      <select
                        style={{ fontSize: "13px" }}
                        value={addForm.strand}
                        disabled={loading}
                        onChange={e => {
                          const value = e.target.value;

                          setAddForm(prev => ({
                            ...prev,
                            strand: value,
                            substrand: ""
                          }));

                          loadSubStrands(value);
                        }}
                      >
                        {strands.map(s => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <td align="right" style={{ fontSize: "14px" }}>Substrand:</td>
                    <td>
                      <select
                        style={{ fontSize: "13px" }}
                        value={addForm.substrand}
                        disabled={loading}
                        onChange={e =>
                          setAddForm(prev => ({
                            ...prev,
                            substrand: e.target.value
                          }))
                        }
                      >
                        {subStrands.map(s => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <td align="right" style={{ fontSize: "14px" }}>Year:</td>
                    <td>
                      <select
                        style={{ fontSize: "13px" }}
                        value={addForm.year}
                        onChange={e => setAddForm({ ...addForm, year: e.target.value })}
                      >
                        {["00", "01", "02", "03", "04", "05", "06", "07", "08", "09", "10"].map(x => (
                          <option key={x} value={`K${x}`}>{x}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <td align="right" valign="top" style={{ fontSize: "14px", paddingTop: "8px" }}>
                      Choose Md lesson you want<br />to add:
                    </td>
                    <td valign="top" style={{ paddingTop: "8px" }}>
                      <div>
                        <input
                          type="text"
                          value={lessonSearch}
                          onChange={e => searchLesson(e.target.value)}
                          placeholder="Search lesson code..."
                          style={{
                            fontSize: "13px",
                            width: "180px",
                            marginBottom: "4px"
                          }}
                        />

                        <br />

                        <select
                          style={{ fontSize: "13px", minWidth: "150px" }}
                          value={addForm.lesson}
                          onChange={e =>
                            setAddForm(prev => ({
                              ...prev,
                              lesson: e.target.value
                            }))
                          }
                        >
                          <option value="">--SELECT--</option>

                          {blobLessons.map(l => (
                            <option key={l} value={l}>
                              {l}
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div style={{ textAlign: "center", marginTop: "15px" }}>
                <button
                  onClick={handleAddLiveTopic}
                  style={{ marginRight: "10px", width: "80px", cursor: "pointer" }}
                >
                  OK
                </button>
                <button
                  onClick={() => setShowAddModal(false)}
                  style={{ width: "80px", cursor: "pointer" }}
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---- MODAL: Update Live Topic ---- */}
      {showUpdateModal && (
        <div style={overlayStyle}>
          <div style={modalBoxStyle}>
            <div style={modalHeaderStyle}>Update MD Lesson</div>
            <div style={modalContentStyle}>
              <div style={{ textAlign: "center", color: "#444", marginBottom: "15px", fontSize: "14px" }}>
                Please select the MD lesson you want to update.
              </div>

              {/* Show which topic is being updated — mirrors VB linkedlsns span */}
              {selectedRow && (
                <div style={{ textAlign: "center", marginBottom: "10px", fontWeight: "bold", fontSize: "13px" }}>
                  Updating: {selectedRow.LessonCode}
                  {/* <Loading /> */}
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginBottom: "20px" }}>
                <span style={{ fontWeight: "bold", marginRight: "10px", fontSize: "14px" }}>MD Lesson:</span>
                <div>
                  <input
                    type="text"
                    value={updateLessonSearch}
                    onChange={e => searchUpdateLesson(e.target.value)}
                    placeholder="Search lesson code..."
                    style={{
                      fontSize: "13px",
                      width: "220px",
                      display: "block",
                      marginBottom: "5px"
                    }}
                  />

                  <select
                    style={{ fontSize: "13px", minWidth: "220px" }}
                    value={updateForm.sourceLesson}
                    onChange={e =>
                      setUpdateForm({
                        sourceLesson: e.target.value
                      })
                    }
                  >
                    <option value="">--SELECT--</option>

                    {blobLessons.map(l => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ textAlign: "center" }}>
                <button
                  onClick={handleUpdateLiveTopic}
                  style={{ marginRight: "10px", width: "80px", cursor: "pointer" }}
                >
                  OK
                </button>
                <button
                  onClick={() => setShowUpdateModal(false)}
                  style={{ width: "80px", cursor: "pointer" }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---- MODAL: Success ---- */}
      {showSuccessModal && (
        <div style={overlayStyle}>
          <div style={{ ...modalBoxStyle, width: "420px" }}>
            <div style={{ ...modalHeaderStyle, borderBottom: "1px solid #c3e6cb" }}>Success</div>
            <div style={{ padding: "20px", textAlign: "center", fontSize: "16px" }}>
              {showSuccessModal}
            </div>
            <div style={{ textAlign: "center", paddingBottom: "16px" }}>
              <button
                onClick={() => setShowSuccessModal(null)}
                style={{ width: "80px", cursor: "pointer" }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MD_ManageTopics;